import { ScopeMode } from '@/client/custom-roles';
import { ScopeModeExplainer } from '@/components/custom-roles/ScopeModeExplainer';
import { RadioButton } from 'primereact/radiobutton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type ScopeModePanelProps = {
  value: ScopeMode | null;
  error?: string;
  onChange: (mode: ScopeMode) => void;
};

const MODES = [ScopeMode.ALL, ScopeMode.GROUPS, ScopeMode.MANAGER];

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  padding: var(--default-padding-small);
  background-color: var(--beige-main);
  border-radius: var(--xsmall-border-radius);

  .p-radiobutton {
    width: 18px;
    height: 18px;
    padding: 0;
    margin: 0;
    flex-shrink: 0;

    .p-radiobutton-box {
      width: 18px;
      height: 18px;
      border: 1.333px solid var(--gray-dark);
      border-radius: 50%;
      background-color: var(--white-main);

      .p-radiobutton-icon {
        display: none;
      }
    }

    &.p-highlight .p-radiobutton-box,
    &.p-highlight:not(.p-disabled):hover .p-radiobutton-box {
      border: 5px solid var(--red-main);
      background-color: var(--white-main);
    }
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--semibold-font-weight);
  line-height: var(--xsmall-line-height);
`;

const Subtitle = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--xsmall-line-height);
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--default-padding-small); /* Figma 21px -> 20px */
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding); /* Figma 7px -> --xsmall-padding (8px) */
`;

const OptionLabel = styled.label`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--xsmall-line-height);
  cursor: pointer;
`;

const ErrorText = styled.span`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--red-main);
`;

export const ScopeModePanel: React.FC<ScopeModePanelProps> = ({
  value,
  error,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Panel>
      <Heading>
        <Title>{t('account.rolesAccess.builder.scope.title')}</Title>
        <Subtitle>{t('account.rolesAccess.builder.scope.subtitle')}</Subtitle>
      </Heading>

      <Options>
        {MODES.map((mode) => (
          <Option key={mode}>
            <RadioButton
              inputId={`scopeMode-${mode}`}
              name="scopeMode"
              value={mode}
              checked={value === mode}
              onChange={() => onChange(mode)}
            />
            <OptionLabel htmlFor={`scopeMode-${mode}`}>
              {t(`account.rolesAccess.scopeMode.${mode}`)}
            </OptionLabel>
          </Option>
        ))}
      </Options>

      {value && <ScopeModeExplainer mode={value} />}

      {error && <ErrorText>{error}</ErrorText>}
    </Panel>
  );
};
